/**
 * Map renderer. Draws a vessel's GPS track on a Leaflet basemap: a time-ordered
 * polyline plus per-point markers, optionally colored by a third column
 * (reusing the scatter color machinery). Leaflet, MapLibre GL and the plugin
 * that bridges them are lazy-loaded the first time a map is drawn, so they stay
 * out of the main bundle.
 *
 * The basemap is OpenFreeMap: keyless, unmetered vector tiles rendered by
 * MapLibre GL inside a Leaflet layer, so the track, markers, tooltips and
 * controls above it remain ordinary Leaflet. maplibre-gl is held at v5 on
 * purpose: with v6 the bridge plugin's basemap never loads (its worker rejects
 * with "can't serialize object of unregistered class Object") and the map draws
 * the track over blank space.
 *
 * **`MapView` is per-host, not a singleton.** Every other renderer here writes
 * through the single `runtime.instance` / `runtime.rootEl` pair, which is why
 * dashboard tiles keep their own uPlot instances (`dashboard/render-tile.ts`).
 * A map has the same problem and the same answer: one `MapView` owns one
 * element, its own Leaflet map, its own layers and its own observers, so a tile
 * and Explore can draw maps at the same time without touching each other. It
 * also takes the `PlotState` to draw with rather than reading `plotState`,
 * since a tile's state is resolved from its own recipe.
 *
 * `renderMap` / `destroyMap` are the Explore-side wrappers: one `MapView` on
 * `runtime.rootEl`, drawn from the global atoms, with the gradient legend going
 * to the strip under the chart. The map owns `#chart-root` while it is up (any
 * uPlot instance is destroyed first), so `destroyMap` gives the DOM back when
 * switching to a uPlot plot type.
 */

import type * as L from 'leaflet';
import type { maplibreGL as MaplibreGLFactory } from '@maplibre/maplibre-gl-leaflet';
import { plotState, type PlotState } from '../../../state/plot-state.ts';
import { runtime } from '../plot-runtime.ts';
import { chartReady, colorLegend, type ColorLegend } from '../plot-ui-state.ts';
import {
  PLOT_COLORS,
  DISCRETE_SCALE,
  isDiscreteScale,
  distinctColorValues,
  colorScaleGradientCss,
  formatColorValue,
} from '../plot-constants.ts';
import { colorScaleRgb, discreteColors } from './color-scale.ts';
import { getCssVar } from './chart-dom.ts';
import { fmtTimeFull, escHtml } from './format.ts';
import { getTheme, type Theme } from '../../../ui/theme.ts';
import type { MapResponse } from '../../../api/types.ts';

/* ── Basemaps ───────────────────────────────────────────── */

/** OpenFreeMap vector styles - no API key, no account, no request quota.
 *  `positron` / `dark` are the OpenMapTiles light and dark basemaps. */
const BASEMAPS = {
  light: { style: 'https://tiles.openfreemap.org/styles/positron' },
  dark: { style: 'https://tiles.openfreemap.org/styles/dark' },
} as const;

const BASEMAP_ATTR =
  '<a href="https://openfreemap.org" target="_blank" rel="noreferrer">OpenFreeMap</a> ' +
  '<a href="https://www.openmaptiles.org/" target="_blank" rel="noreferrer">© OpenMapTiles</a> ' +
  'Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>';

/** Own pane for the GL basemap, below Leaflet's tilePane (200) so the seamark
 *  overlay and the track stay on top however often the basemap is swapped -
 *  the GL layer is not a GridLayer and has no `bringToBack()`. */
const BASEMAP_PANE = 'ofm-basemap';
const BASEMAP_PANE_Z = 190;

/** Vector tiles stop at z14 but overzoom cleanly; this is the Leaflet view cap. */
const MAX_ZOOM = 19;
/** The deepest the view fits a track by itself. A ship that has not moved is one
 *  point, which Leaflet would fit at MAX_ZOOM, where the to-scale hull is wider
 *  than the map. At 16 a 310 m ship is about 200 px. */
const FIT_MAX_ZOOM = 16;

const SEAMARK_URL = 'https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png';
const SEAMARK_ATTR = '© OpenSeaMap';

/** Track points more than this many seconds apart (in `time`, epoch seconds)
 *  are not connected by the track line - bridges ordinary reporting gaps
 *  (missing pings) instead of drawing a spurious line across them. */
const TRACK_GAP_SECONDS = 1.5 * 60 * 60;

/** Above this implied speed (knots) between two consecutive points, the track
 *  line is not drawn between them even if the time gap is under
 *  TRACK_GAP_SECONDS - cruise ships cannot travel this fast, so a short-but-huge
 *  jump means bad/glitched position data rather than continuous sailing. Set
 *  to the maximum realistic cruise ship speed (~25 kn). */
const MAX_SHIP_SPEED_KNOTS = 25;

const EARTH_RADIUS_NM = 3440.065;

/** Great-circle distance between two lat/lon points, in nautical miles. */
function haversineNm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_NM * Math.asin(Math.sqrt(a));
}

/* ── Lazy module loading (shared by every MapView) ──────── */

let Lmod: typeof L | null = null;
let maplibreGL: typeof MaplibreGLFactory | null = null;

async function ensureLeaflet(): Promise<typeof L> {
  if (Lmod && maplibreGL) return Lmod;
  // The plugin imports leaflet and maplibre-gl itself, so everything below
  // lands in the same lazily-loaded chunk.
  const [mod, glMod] = await Promise.all([
    import('leaflet'),
    import('@maplibre/maplibre-gl-leaflet'),
    import('leaflet/dist/leaflet.css'),
    import('maplibre-gl/dist/maplibre-gl.css'),
  ]);
  Lmod = (mod as unknown as { default: typeof L }).default ?? (mod as unknown as typeof L);
  maplibreGL = glMod.maplibreGL;
  return Lmod;
}

/* ── Color mapping (continuous gradient or discrete palette) ── */

interface ColorPlan {
  colorAt: (i: number) => string;
  /** Discrete legend rows, or null when the gradient (continuous) legend is used. */
  discrete: Array<{ label: string; color: string }> | null;
  min: number;
  max: number;
}

function buildColorPlan(data: MapResponse, scaleName: string): ColorPlan | null {
  const vals = data.color_values;
  if (!vals || vals.length !== data.lats.length) return null;

  const categorical =
    !!data.color_categorical || vals.some((v) => typeof v === 'string');

  // Discrete (categorical): one palette color per distinct value - for a
  // categorical column, or a "Discrete*" scale on a low-cardinality numeric
  // column. Numeric columns with too many distinct values fall to a gradient.
  const wantDiscrete = categorical || isDiscreteScale(scaleName);
  const distinct = wantDiscrete ? distinctColorValues(vals, PLOT_COLORS.length) : null;
  if (distinct) {
    // Categorical forced onto a gradient scale → qualitative palette; an
    // explicit "Discrete:<Colormap>" samples that map.
    const paletteScale = isDiscreteScale(scaleName) ? scaleName : DISCRETE_SCALE;
    const palette = discreteColors(paletteScale, distinct.length);
    const colorAt = (i: number): string =>
      palette[i] ?? PLOT_COLORS[i % PLOT_COLORS.length]!;
    const idxOf = new Map<number | string, number>();
    distinct.forEach((v, i) => idxOf.set(v, i));
    return {
      colorAt: (i) => colorAt(vals[i] != null ? idxOf.get(vals[i]!) ?? 0 : 0),
      discrete: distinct.map((v, i) => ({ label: formatColorValue(v), color: colorAt(i) })),
      min: 0,
      max: 0,
    };
  }

  // Continuous gradient (numeric only).
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of vals) {
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (lo === Infinity) return null;
  if (hi === lo) hi = lo + 1;
  return {
    colorAt: (i) => {
      const v = vals[i];
      if (typeof v !== 'number' || !Number.isFinite(v)) return '#888';
      const t = Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
      return colorScaleRgb(scaleName, t);
    },
    discrete: null,
    min: lo,
    max: hi,
  };
}

/* ── MapView ────────────────────────────────────────────── */

export interface MapViewOptions {
  /** Pan and zoom by hand. Off makes the map a picture of the track. */
  interactive?: boolean;
  /** Wheel zoom. Off on a dashboard, where the wheel belongs to the page. */
  scrollWheelZoom?: boolean;
  /** Leaflet's +/- control. */
  zoomControl?: boolean;
  /**
   * Keep the GL basemap readable after it has been composited.
   *
   * A WebGL canvas is cleared once the browser has drawn it, so `drawImage`
   * from it gives back an empty rectangle - which is exactly what a dashboard
   * export does. Costs a little memory, so it is off unless someone will copy
   * the pixels.
   */
  preserveDrawingBuffer?: boolean;
  /**
   * Where a continuous (gradient) legend goes. Explore hands it to the strip
   * under the chart; a tile passes nothing, because a gradient has no room to
   * explain itself there. A discrete legend is drawn on the map either way.
   */
  onColorLegend?: ((legend: ColorLegend | null) => void) | null;
  /** Element to watch for size changes, when it is not the host itself. */
  resizeTarget?: HTMLElement | null;
}

/**
 * One Leaflet map on one element. Construct with `createMapView` (which loads
 * Leaflet first), then `render` as often as the data or the state changes.
 */
export class MapView {
  private map: L.Map;
  /** The MapLibre GL basemap layer (a plain `L.Layer`, not a tile layer). */
  private baseLayer: L.Layer | null = null;
  /** Theme the current basemap was built for - so re-renders don't reload tiles. */
  private baseTheme: Theme | null = null;
  private seamarkLayer: L.TileLayer | null = null;
  private trackLayer: L.LayerGroup;
  /** The ship on the track, redrawn on zoom because its size is to scale. */
  private ship: { marker: L.Marker; heading: number; lat: number } | null = null;
  private legendControl: L.Control | null = null;
  private themeObs: MutationObserver | null = null;
  private resizeObs: ResizeObserver | null = null;
  /** The data last drawn - used to fit bounds only when the data changes (a
   *  color/track/nautical tweak re-renders the same object → keep the view). */
  private lastData: MapResponse | null = null;
  /** The track's extent, kept so a resize can re-fit to it. */
  private fitTo: L.LatLngBounds | null = null;
  /**
   * True once the viewer has panned or zoomed by hand, after which the view is
   * theirs: a resize re-fits the track only until then. A tile is measured,
   * mounted and laid out over several frames, so the first fit routinely lands
   * against a box that is not its final size - without this the track ends up a
   * speck in the middle of a continent.
   */
  private userMoved = false;
  private destroyed = false;

  constructor(
    private readonly leaflet: typeof L,
    private readonly host: HTMLElement,
    private readonly options: MapViewOptions,
  ) {
    const interactive = options.interactive !== false;
    this.map = leaflet.map(host, {
      preferCanvas: true,
      worldCopyJump: true,
      maxZoom: MAX_ZOOM,
      zoomControl: options.zoomControl !== false,
      dragging: interactive,
      doubleClickZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
      touchZoom: interactive,
      scrollWheelZoom: interactive && options.scrollWheelZoom !== false,
    });
    this.map.createPane(BASEMAP_PANE).style.zIndex = String(BASEMAP_PANE_Z);
    this.applyBasemap();
    this.trackLayer = leaflet.layerGroup().addTo(this.map);

    // Swap the basemap when the global light/dark toggle flips (a <html> class
    // change that no atom tracks - same approach as the SFOC viewer).
    this.themeObs = new MutationObserver(() => this.applyBasemap());
    this.themeObs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Keep Leaflet sized to its container as the layout changes.
    const target = options.resizeTarget ?? host;
    this.resizeObs = new ResizeObserver(() => this.invalidateSize());
    this.resizeObs.observe(target);

    // Panning, zooming or wheeling by hand hands the view over for good.
    // Watched on the container rather than through Leaflet's move events,
    // which cannot tell a `fitBounds` from a drag.
    const claim = (): void => {
      this.userMoved = true;
    };
    host.addEventListener('pointerdown', claim);
    host.addEventListener('wheel', claim, { passive: true });
    this.map.on('zoomstart', () => {
      // A zoom from the +/- control or a double-click is also the viewer's.
      if (!this.fitting) this.userMoved = true;
    });
    this.map.on('zoomend', () => {
      if (this.ship) this.ship.marker.setIcon(this.shipIcon(this.ship.heading, this.ship.lat));
    });
  }

  /** True while `fitBounds` is running, so its own events are not a user zoom. */
  private fitting = false;

  /**
   * Fit to the track, marking the move as ours rather than the viewer's.
   *
   * Unanimated on purpose: an animated fit fires `zoomstart` on a later frame,
   * by which time the flag below has been cleared and the fit reads as the
   * viewer grabbing the map - after which the view stops following the track.
   */
  private fit(bounds: L.LatLngBounds): void {
    this.fitting = true;
    try {
      this.map.fitBounds(bounds, { padding: [24, 24], animate: false, maxZoom: FIT_MAX_ZOOM });
    } finally {
      this.fitting = false;
    }
  }

  /** Draw `data` with `state`'s styling. Re-fits the view only on new data. */
  render(data: MapResponse, state: PlotState): void {
    if (this.destroyed) return;
    // A sliced copy shares the arrays of the response it came from: same data,
    // so a slicer drag keeps the view.
    const isNewData = data.lats !== this.lastData?.lats;
    this.applyBasemap();
    this.applySeamarks(state);
    const bounds = this.drawTrack(data, state);

    // New data is a new view: fit it, and let a resize keep fitting it until
    // the viewer takes the view over.
    if (isNewData && bounds) {
      this.fitTo = bounds;
      this.userMoved = false;
    }

    // Size the map to its container BEFORE fitting bounds - fitting against a
    // stale (collapsed) size lands the view in the wrong place and leaves tile
    // seams until the next pan.
    this.map.invalidateSize();
    if (isNewData && bounds) this.fit(bounds);
    this.lastData = data;

    // Belt-and-suspenders: re-measure once layout settles (card just became
    // visible, fonts, late reflow) so no tile gaps remain.
    requestAnimationFrame(() => this.invalidateSize());
  }

  /**
   * Re-measure, and re-fit the track while the view is still ours. Called from
   * the resize observer and by the host after a layout change.
   */
  invalidateSize(): void {
    if (this.destroyed) return;
    this.map.invalidateSize();
    if (this.fitTo && !this.userMoved) this.fit(this.fitTo);
  }

  /** Release the Leaflet map, its observers and the host's children. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.themeObs?.disconnect();
    this.themeObs = null;
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.map.remove();
    this.baseLayer = null;
    this.baseTheme = null;
    this.seamarkLayer = null;
    this.legendControl = null;
    this.lastData = null;
    this.fitTo = null;
    this.options.onColorLegend?.(null);
  }

  /* ── Layers ───────────────────────────────────────────── */

  /** Build the GL basemap layer for a theme, in its own pane. */
  private basemapLayer(theme: Theme): L.Layer {
    return maplibreGL!({
      style: BASEMAPS[theme].style,
      attribution: BASEMAP_ATTR,
      pane: BASEMAP_PANE,
      // maplibre-gl v5 moved the WebGL context flags here from the top level;
      // passing `preserveDrawingBuffer` beside `style` is silently ignored.
      ...(this.options.preserveDrawingBuffer
        ? { canvasContextAttributes: { preserveDrawingBuffer: true } }
        : {}),
    } as Parameters<typeof MaplibreGLFactory>[0]);
  }

  /** Swap the basemap when the theme changed (preserves the view). No-op when
   *  the current basemap already matches, so re-renders don't reload tiles. */
  private applyBasemap(): void {
    if (this.destroyed) return;
    const theme = getTheme();
    if (this.baseLayer && this.baseTheme === theme) return;
    if (this.baseLayer) this.map.removeLayer(this.baseLayer);
    this.baseLayer = this.basemapLayer(theme).addTo(this.map);
    this.baseTheme = theme;
  }

  /** Add/remove the OpenSeaMap seamark overlay per the nautical toggle. */
  private applySeamarks(state: PlotState): void {
    const want = state.map_nautical;
    if (want && !this.seamarkLayer) {
      this.seamarkLayer = this.leaflet
        .tileLayer(SEAMARK_URL, {
          attribution: SEAMARK_ATTR,
          maxZoom: 18,
          // Requested with CORS so the tiles do not taint a canvas they are
          // composited into - a dashboard export copies them.
          crossOrigin: true,
        })
        .addTo(this.map);
    } else if (!want && this.seamarkLayer) {
      this.map.removeLayer(this.seamarkLayer);
      this.seamarkLayer = null;
    }
  }

  /* ── Track ────────────────────────────────────────────── */

  /** Draw the polyline + markers + legend; returns the track bounds to fit (or
   *  null when there are no valid points). Fitting is done by the caller after
   *  the map has its final size. */
  private drawTrack(data: MapResponse, s: PlotState): L.LatLngBounds | null {
    const leaflet = this.leaflet;
    this.trackLayer.clearLayers();
    this.ship = null;

    const accent = getCssVar('--accent') || '#6384ff';
    const alpha = Math.max(0.05, Math.min(1, s.style.scatter_alpha ?? 0.7));
    const radius = Math.max(1, s.style.scatter_point_size ?? 3);
    const plan = buildColorPlan(data, s.color_scale || 'Viridis');

    const latlngs: L.LatLngTuple[] = [];
    // Segments split wherever the gap to the previous valid point exceeds
    // TRACK_GAP_SECONDS (bridges ordinary reporting gaps), or the implied
    // speed exceeds MAX_SHIP_SPEED_KNOTS (catches short-time/huge-distance
    // glitches that a time-only threshold can't tell apart from a real leg).
    const segments: L.LatLngTuple[][] = [];
    let currentSegment: L.LatLngTuple[] = [];
    let prev: { lat: number; lon: number; t: number } | null = null;
    const hidden = data.hidden;
    for (let i = 0; i < data.lats.length; i++) {
      const lat = data.lats[i];
      const lon = data.lons[i];
      if (hidden?.[i]) {
        // A point a slicer hides ends the line: no bridge across what is cut out.
        if (currentSegment.length) segments.push(currentSegment);
        currentSegment = [];
        prev = null;
        continue;
      }
      if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      latlngs.push([lat, lon]);

      const t = data.time[i];
      const hasTime = t != null && Number.isFinite(t);
      if (prev && hasTime) {
        const dt = t - prev.t;
        const hours = dt / 3600;
        const nm = haversineNm(prev.lat, prev.lon, lat, lon);
        const knots = hours > 0 ? nm / hours : Infinity;
        const tooSlow = Math.abs(dt) > TRACK_GAP_SECONDS;
        const tooFast = nm > 0.1 && knots > MAX_SHIP_SPEED_KNOTS;
        if (tooSlow || tooFast) {
          if (currentSegment.length) segments.push(currentSegment);
          currentSegment = [];
        }
      }
      currentSegment.push([lat, lon]);
      if (hasTime) prev = { lat, lon, t };
    }
    if (currentSegment.length) segments.push(currentSegment);
    if (!latlngs.length) return null;

    // Connecting track (drawn under the points), one polyline per segment so
    // gaps longer than TRACK_GAP_SECONDS aren't bridged by a line.
    if (s.map_show_track) {
      for (const segment of segments) {
        if (segment.length < 2) continue;
        leaflet
          .polyline(segment, { color: accent, weight: 2, opacity: 0.55 })
          .addTo(this.trackLayer);
      }
    }

    // Per-point markers (canvas-rendered) with a hover tooltip.
    const cb = data.color_by;
    for (let i = 0; i < data.lats.length; i++) {
      const lat = data.lats[i];
      const lon = data.lons[i];
      if (hidden?.[i] || lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      const color = plan ? plan.colorAt(i) : accent;
      const marker = leaflet.circleMarker([lat, lon], {
        radius,
        color,
        weight: 0,
        fillColor: color,
        fillOpacity: alpha,
      });
      marker.bindTooltip(pointTooltip(data, i, cb), { direction: 'top', sticky: true });
      marker.addTo(this.trackLayer);
    }

    this.drawShip(data);

    this.updateLegend(cb, plan, s.color_scale || 'Viridis');

    return leaflet.latLngBounds(latlngs);
  }

  /** The ship (sketch round 5, hull D) on the last point the slicers keep,
   *  bow turned to its heading. No heading there, no ship: a guessed bow would lie. */
  private drawShip(data: MapResponse): void {
    let i = data.lats.length - 1;
    while (i >= 0 && (data.hidden?.[i] || !(Number.isFinite(data.lats[i]) && Number.isFinite(data.lons[i])))) i--;
    const lat = data.lats[i];
    const lon = data.lons[i];
    const heading = data.headings?.[i];
    if (lat == null || lon == null || heading == null || !Number.isFinite(heading)) return;
    // Not interactive: hovering it reaches the point marker underneath.
    const marker = this.leaflet
      .marker([lat, lon], { icon: this.shipIcon(heading, lat), interactive: false, keyboard: false })
      .addTo(this.trackLayer);
    this.ship = { marker, heading, lat };
  }

  /** The hull at this zoom: its true length, never under SHIP_MIN_PX. */
  private shipIcon(heading: number, lat: number): L.DivIcon {
    // No zoom before the first fit; that fit's zoomend draws the hull again.
    const zoom = this.map.getZoom();
    const metresPerPx = (40075016.686 * Math.cos((lat * Math.PI) / 180)) / 2 ** (zoom + 8);
    const lengthPx = Number.isFinite(zoom) ? Math.max(SHIP_LENGTH_M / metresPerPx, SHIP_MIN_PX) : SHIP_MIN_PX;
    const { html, size } = shipSvg(heading, lengthPx);
    return this.leaflet.divIcon({
      className: 'map-ship',
      html,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  /**
   * Legend: discrete → an on-map control, wherever the map is drawn. Continuous
   * → whatever `onColorLegend` does with it (Explore: the gradient strip under
   * the chart; a tile: nothing, so the gradient is simply not explained).
   */
  private updateLegend(cb: string | null, plan: ColorPlan | null, scaleName: string): void {
    const leaflet = this.leaflet;
    if (this.legendControl) {
      this.map.removeControl(this.legendControl);
      this.legendControl = null;
    }
    if (!cb || !plan) {
      this.options.onColorLegend?.(null);
      return;
    }
    if (plan.discrete) {
      this.options.onColorLegend?.(null);
      const Control = leaflet.Control.extend({
        onAdd(): HTMLElement {
          const div = leaflet.DomUtil.create('div', 'map-legend');
          const rows = plan
            .discrete!.map(
              (d) =>
                `<div class="map-legend-row"><span class="map-legend-swatch" style="background:${d.color}"></span>${escHtml(
                  d.label,
                )}</div>`,
            )
            .join('');
          div.innerHTML = `<div class="map-legend-title">${escHtml(cb)}</div>${rows}`;
          return div;
        },
      });
      this.legendControl = new Control({ position: 'bottomright' });
      this.legendControl.addTo(this.map);
    } else {
      this.options.onColorLegend?.({
        colorBy: cb,
        min: Number(plan.min.toPrecision(4)),
        max: Number(plan.max.toPrecision(4)),
        gradientCss: colorScaleGradientCss(scaleName),
      });
    }
  }
}

/**
 * Mount a map on `host`, loading Leaflet first. The host is emptied and given
 * the `leaflet-map` class, so the caller only has to provide a sized element.
 */
export async function createMapView(
  host: HTMLElement,
  options: MapViewOptions = {},
): Promise<MapView> {
  const leaflet = await ensureLeaflet();
  host.innerHTML = '';
  host.classList.add('leaflet-map');
  return new MapView(leaflet, host, options);
}

// shortcut: one size for every ship (310 x 38.75 m), until we have length and beam per ship.
const SHIP_LENGTH_M = 310;
const SHIP_BEAM_RATIO = 1 / 8;
/** The fix is the GPS antenna on the bridge, this share of the length behind the bow. */
const SHIP_BRIDGE = 0.15;
/** Zoomed out, the hull stays this long so it can be seen. */
const SHIP_MIN_PX = 46;
const SHIP_MIN_BEAM_PX = 7;

/** Top view of a hull `lengthPx` long, bow turned to `heading` (degrees
 *  clockwise from north), in a square box centred on the fix (the bridge).
 *  The shape is Thruster Explorer's `TX.hullPath`: fine bow, straight sides,
 *  flat transom with round corners. */
export function shipSvg(heading: number, lengthPx: number): { html: string; size: number } {
  const h = lengthPx / 2;
  const b = Math.max(lengthPx * SHIP_BEAM_RATIO, SHIP_MIN_BEAM_PX) / 2;
  const f = (v: number): string => v.toFixed(1);
  const hull =
    `M0 ${f(-h)} C${f(b * 0.9)} ${f(-h * 0.82)} ${f(b)} ${f(-h * 0.55)} ${f(b)} ${f(-h * 0.3)} ` +
    `L${f(b)} ${f(h * 0.9)} Q${f(b)} ${f(h)} ${f(b * 0.7)} ${f(h)} L${f(-b * 0.7)} ${f(h)} ` +
    `Q${f(-b)} ${f(h)} ${f(-b)} ${f(h * 0.9)} L${f(-b)} ${f(-h * 0.3)} ` +
    `C${f(-b)} ${f(-h * 0.55)} ${f(-b * 0.9)} ${f(-h * 0.82)} 0 ${f(-h)} Z`;
  // The stern is the far end from the fix; the box holds it at any heading.
  const size = Math.ceil(2 * (1 - SHIP_BRIDGE) * lengthPx + 8);
  const fixY = f(-h + SHIP_BRIDGE * lengthPx);
  const html =
    `<svg width="${size}" height="${size}" viewBox="${-size / 2} ${-size / 2} ${size} ${size}" aria-hidden="true">` +
    `<g transform="rotate(${heading.toFixed(1)}) translate(0 ${f((0.5 - SHIP_BRIDGE) * lengthPx)})">` +
    `<path class="map-ship-hull" d="${hull}"/>` +
    `<rect class="map-ship-deck" x="${f(-b * 0.6)}" y="${f(-lengthPx * 0.25)}" width="${f(b * 1.2)}" height="${f(lengthPx * 0.55)}"/>` +
    `<circle class="map-ship-fix" cx="0" cy="${fixY}" r="3.6"/>` +
    `<circle class="map-ship-fix-dot" cx="0" cy="${fixY}" r="1.2"/>` +
    `</g></svg>`;
  return { html, size };
}

function pointTooltip(data: MapResponse, i: number, cb: string | null): string {
  const t = data.time?.[i];
  const rows: string[] = [];
  // `time` is epoch SECONDS (LTTB x for a temporal column); fmtTimeFull wants seconds.
  if (t != null && Number.isFinite(t)) rows.push(escHtml(fmtTimeFull(t)));
  if (cb && data.color_values) {
    const v = data.color_values[i];
    if (v != null && (typeof v === 'string' || Number.isFinite(v))) {
      rows.push(`<strong>${escHtml(cb)}</strong>: ${escHtml(formatColorValue(v))}`);
    }
  }
  const lat = data.lats[i];
  const lon = data.lons[i];
  rows.push(`${escHtml((lat ?? 0).toFixed(4))}, ${escHtml((lon ?? 0).toFixed(4))}`);
  return rows.join('<br>');
}

/* ── Explore's single map ───────────────────────────────── */

let exploreView: MapView | null = null;
/** The host div inside `#chart-root`, so `destroyMap` can take it back out. */
let exploreHost: HTMLElement | null = null;
/** Guards against two fire-and-forget renders racing through `ensureLeaflet`. */
let exploreToken = 0;

export function renderMap(data: MapResponse): void {
  // Async because Leaflet is lazy-loaded; renderChart calls this fire-and-forget.
  void renderMapAsync(data);
}

async function renderMapAsync(data: MapResponse): Promise<void> {
  const token = ++exploreToken;
  const root = runtime.rootEl;
  if (!root) return;

  // Any uPlot instance must go before the map takes over #chart-root.
  if (runtime.instance) {
    runtime.instance.destroy();
    runtime.instance = null;
  }

  if (!exploreView) {
    root.innerHTML = '';
    const host = document.createElement('div');
    host.className = 'leaflet-map';
    root.appendChild(host);
    const view = await createMapView(host, {
      resizeTarget: runtime.containerEl ?? host,
      onColorLegend: (legend) => colorLegend.set(legend),
    });
    // Another render (or a plot-type switch) got there first while Leaflet was
    // loading: drop this one rather than leaving two maps on one root.
    if (token !== exploreToken) {
      view.destroy();
      host.remove();
      return;
    }
    exploreView = view;
    exploreHost = host;
  }

  exploreView.render(data, plotState.value);
  // The map owns its own pixels and can't feed the uPlot image-export path, so
  // keep the export menu hidden for map plots.
  chartReady.set(false);
}

/** Tear Explore's map down and release its DOM (switching plot type). */
export function destroyMap(): void {
  exploreToken++;
  exploreView?.destroy();
  exploreView = null;
  // Remove the host div we appended so a following uPlot render starts clean.
  exploreHost?.remove();
  exploreHost = runtime.rootEl?.querySelector<HTMLElement>('.leaflet-map') ?? null;
  exploreHost?.remove();
  exploreHost = null;
  colorLegend.set(null);
}
